import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FiMinimize2 } from "react-icons/fi";
import { EditorContent, useEditor } from "@tiptap/react";
import { createEditorExtensions } from "components/common/textEditor/editorExtensions";
import {
  DEFAULT_TOOLBAR_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_OPTIONS,
  HEADING_LEVELS,
} from "components/common/textEditor/toolbarConfig";

function ToolbarButton({ label, onClick, isActive = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "rounded-md border px-2 py-1 text-xs font-medium transition",
        "border-slate-200 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50",
        "dark:border-slate-700 dark:hover:bg-slate-700",
        isActive ? "bg-brand-100 text-brand-700 dark:bg-slate-700 dark:text-white" : "text-text-700 dark:text-slate-100",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

ToolbarButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default function CommonTextEditorInner({
  value,
  onChange,
  placeholder = "Write something...",
  readOnly = false,
  height = 360,
  toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
  error = "",
  disabled = false,
  characterLimit = 10000,
  editorFieldId = "",
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTextColor, setActiveTextColor] = useState("#1f2937");
  const [activeBgColor, setActiveBgColor] = useState("#ffffff");
  const fileInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  /** When `value` matches this, the update came from this editor — do not `setContent` or focus/cursor is lost. */
  const lastEmittedHtmlRef = useRef(null);

  const extensions = useMemo(
    () => createEditorExtensions({ placeholder, characterLimit }),
    [placeholder, characterLimit],
  );

  const editorFieldProps = useMemo(() => {
    if (!editorFieldId) return undefined;
    return { editorProps: { attributes: { id: editorFieldId } } };
  }, [editorFieldId]);

  const mergedToolbarOptions = useMemo(() => {
    const next = { ...DEFAULT_TOOLBAR_OPTIONS };
    if (toolbarOptions && typeof toolbarOptions === "object") {
      Object.assign(next, toolbarOptions);
    }
    return next;
  }, [toolbarOptions]);

  /** Client-only CRA: immediate render avoids `Editor | null` + StrictMode teardown races from `immediatelyRender: false`. */
  const editor = useEditor(
    {
      extensions,
      content: value || "",
      editable: !readOnly && !disabled,
      onUpdate: ({ editor: currentEditor }) => {
        const html = currentEditor.getHTML();
        lastEmittedHtmlRef.current = html;
        onChangeRef.current(html, currentEditor);
      },
      ...(editorFieldProps ?? undefined),
    },
    [extensions, editorFieldId],
  );

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (lastEmittedHtmlRef.current === next) {
      lastEmittedHtmlRef.current = null;
      return;
    }
    if (editor.getHTML() === next) {
      return;
    }
    editor.commands.setContent(next, { emitUpdate: false });
    lastEmittedHtmlRef.current = null;
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly && !disabled);
  }, [editor, readOnly, disabled]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };
    if (!isFullscreen) return undefined;
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  if (!editor) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-text-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
        Loading editor...
      </div>
    );
  }

  const isUiDisabled = readOnly || disabled;
  const characterCount = editor.storage.characterCount.characters();

  const run = (callback) => {
    if (isUiDisabled) return;
    callback();
  };

  const applyLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", previousUrl || "https://");
    if (url === null) return;
    if (!url) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read image file."));
      reader.readAsDataURL(file);
    });
    editor.chain().focus().setImage({ src: String(base64), alt: file.name }).run();
    event.target.value = "";
  };

  const insertYoutube = () => {
    const url = window.prompt("Enter YouTube URL");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url, width: 640, height: 360 }).run();
  };

  const insertMention = () => {
    const mention = window.prompt("Mention username without @", "admin");
    if (!mention) return;
    editor.chain().focus().insertContent(`@${mention} `).run();
  };

  const insertEmoji = () => {
    const emoji = window.prompt("Insert emoji", "😊");
    if (!emoji) return;
    editor.chain().focus().insertContent(emoji).run();
  };

  const toolbarGroupClass = "flex flex-wrap items-center gap-1 border-r border-slate-200 pr-2 last:border-r-0 dark:border-slate-700";

  const innerShellClass = isFullscreen
    ? "fixed inset-0 z-[100] flex min-h-0 flex-col overflow-hidden bg-white p-3 shadow-none dark:bg-slate-900"
    : "flex min-h-0 flex-col";

  return (
    <div
      className={[
        "common-text-editor rounded-xl border bg-white shadow-card transition",
        "dark:border-slate-700 dark:bg-slate-800",
        error ? "border-red-500 ring-1 ring-red-400" : "border-slate-200",
        isFullscreen ? "relative min-h-0 border-0 bg-transparent p-0 shadow-none dark:bg-transparent" : "",
      ].join(" ")}
    >
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />

      <div className={innerShellClass}>
        {isFullscreen && (
          <div className="mb-2 flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 pb-2 dark:border-slate-700">
            <span className="text-sm font-semibold text-text-700 dark:text-slate-100">Fullscreen editor</span>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-text-900 shadow-sm hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              onClick={() => setIsFullscreen(false)}
            >
              <FiMinimize2 className="h-4 w-4 shrink-0" aria-hidden />
              Exit fullscreen
            </button>
          </div>
        )}

        <div className="flex shrink-0 flex-wrap gap-2 border-b border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900">
        {mergedToolbarOptions.history && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="Undo" onClick={() => run(() => editor.chain().focus().undo().run())} disabled={isUiDisabled || !editor.can().undo()} />
            <ToolbarButton label="Redo" onClick={() => run(() => editor.chain().focus().redo().run())} disabled={isUiDisabled || !editor.can().redo()} />
          </div>
        )}

        {mergedToolbarOptions.textStyle && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="B" isActive={editor.isActive("bold")} onClick={() => run(() => editor.chain().focus().toggleBold().run())} disabled={isUiDisabled} />
            <ToolbarButton label="I" isActive={editor.isActive("italic")} onClick={() => run(() => editor.chain().focus().toggleItalic().run())} disabled={isUiDisabled} />
            <ToolbarButton label="U" isActive={editor.isActive("underline")} onClick={() => run(() => editor.chain().focus().toggleUnderline().run())} disabled={isUiDisabled} />
            <select
              disabled={isUiDisabled}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(event) => run(() => editor.chain().focus().setFontFamily(event.target.value).run())}
              value={editor.getAttributes("textStyle").fontFamily || FONT_FAMILY_OPTIONS[0].value}
            >
              {FONT_FAMILY_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <select
              disabled={isUiDisabled}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(event) => run(() => editor.chain().focus().setFontSize(event.target.value).run())}
              value={editor.getAttributes("textStyle").fontSize || FONT_SIZE_OPTIONS[2].value}
            >
              {FONT_SIZE_OPTIONS.map((size) => (
                <option key={size.value} value={size.value}>
                  {size.label}px
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
              <span className="text-text-500 dark:text-slate-300">A</span>
              <input
                type="color"
                value={activeTextColor}
                disabled={isUiDisabled}
                onChange={(event) => {
                  setActiveTextColor(event.target.value);
                  run(() => editor.chain().focus().setColor(event.target.value).run());
                }}
              />
            </label>
            <label className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
              <span className="text-text-500 dark:text-slate-300">BG</span>
              <input
                type="color"
                value={activeBgColor}
                disabled={isUiDisabled}
                onChange={(event) => {
                  setActiveBgColor(event.target.value);
                  run(() => editor.chain().focus().setHighlight({ color: event.target.value }).run());
                }}
              />
            </label>
          </div>
        )}

        {mergedToolbarOptions.headings && (
          <div className={toolbarGroupClass}>
            {HEADING_LEVELS.map((level) => (
              <ToolbarButton
                key={level}
                label={`H${level}`}
                isActive={editor.isActive("heading", { level })}
                onClick={() => run(() => editor.chain().focus().toggleHeading({ level }).run())}
                disabled={isUiDisabled}
              />
            ))}
          </div>
        )}

        {mergedToolbarOptions.alignment && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="L" isActive={editor.isActive({ textAlign: "left" })} onClick={() => run(() => editor.chain().focus().setTextAlign("left").run())} disabled={isUiDisabled} />
            <ToolbarButton label="C" isActive={editor.isActive({ textAlign: "center" })} onClick={() => run(() => editor.chain().focus().setTextAlign("center").run())} disabled={isUiDisabled} />
            <ToolbarButton label="R" isActive={editor.isActive({ textAlign: "right" })} onClick={() => run(() => editor.chain().focus().setTextAlign("right").run())} disabled={isUiDisabled} />
            <ToolbarButton label="J" isActive={editor.isActive({ textAlign: "justify" })} onClick={() => run(() => editor.chain().focus().setTextAlign("justify").run())} disabled={isUiDisabled} />
          </div>
        )}

        {mergedToolbarOptions.lists && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="OL" isActive={editor.isActive("orderedList")} onClick={() => run(() => editor.chain().focus().toggleOrderedList().run())} disabled={isUiDisabled} />
            <ToolbarButton label="UL" isActive={editor.isActive("bulletList")} onClick={() => run(() => editor.chain().focus().toggleBulletList().run())} disabled={isUiDisabled} />
            <ToolbarButton label="Checklist" isActive={editor.isActive("taskList")} onClick={() => run(() => editor.chain().focus().toggleTaskList().run())} disabled={isUiDisabled} />
          </div>
        )}

        {mergedToolbarOptions.insert && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="Link" onClick={() => run(applyLink)} isActive={editor.isActive("link")} disabled={isUiDisabled} />
            <ToolbarButton label="Image" onClick={() => run(insertImage)} disabled={isUiDisabled} />
            <ToolbarButton label="Video" onClick={() => run(insertYoutube)} disabled={isUiDisabled} />
            <ToolbarButton label="Table" onClick={() => run(() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())} disabled={isUiDisabled} />
            <ToolbarButton label="@Mention" onClick={() => run(insertMention)} disabled={isUiDisabled} />
            <ToolbarButton label="Emoji" onClick={() => run(insertEmoji)} disabled={isUiDisabled} />
          </div>
        )}

        {mergedToolbarOptions.blocks && (
          <div className={toolbarGroupClass}>
            <ToolbarButton label="Code" isActive={editor.isActive("codeBlock")} onClick={() => run(() => editor.chain().focus().toggleCodeBlock().run())} disabled={isUiDisabled} />
            <ToolbarButton label="Quote" isActive={editor.isActive("blockquote")} onClick={() => run(() => editor.chain().focus().toggleBlockquote().run())} disabled={isUiDisabled} />
            <ToolbarButton label="P" isActive={editor.isActive("paragraph")} onClick={() => run(() => editor.chain().focus().setParagraph().run())} disabled={isUiDisabled} />
          </div>
        )}

        {mergedToolbarOptions.extras && (
          <div className="ml-auto flex items-center gap-1">
            <ToolbarButton label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} onClick={() => setIsFullscreen((prev) => !prev)} />
          </div>
        )}
      </div>

        <div className={["flex min-h-0 w-full flex-1 flex-col", isFullscreen ? "max-w-6xl self-center" : ""].filter(Boolean).join(" ")}>
          <div
            className={[
              "common-text-editor__content min-h-0 flex-1 overflow-y-auto p-3",
              isFullscreen ? "rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/50" : "",
            ].join(" ")}
            style={isFullscreen ? undefined : { minHeight: `${height}px` }}
          >
            <EditorContent editor={editor} />
          </div>
          <div className="flex shrink-0 items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-text-500 dark:border-slate-700 dark:text-slate-300">
            <span>
              {characterCount}/{characterLimit} characters
            </span>
            <span>{error || "\u00a0"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

CommonTextEditorInner.propTypes = {
  editorFieldId: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  height: PropTypes.number,
  toolbarOptions: PropTypes.shape({
    history: PropTypes.bool,
    textStyle: PropTypes.bool,
    headings: PropTypes.bool,
    alignment: PropTypes.bool,
    lists: PropTypes.bool,
    insert: PropTypes.bool,
    blocks: PropTypes.bool,
    extras: PropTypes.bool,
  }),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  characterLimit: PropTypes.number,
};
